import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useRouter} from 'next/router';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

const STORAGE_KEY = 'sidebarNavCollapsed';
const STORAGE_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * A collapsed rail does two things on hover: it shows a tooltip for the icon
 * under the cursor, and — if you linger — it expands. The expansion delay is
 * therefore much longer than the tooltip's, so sweeping the pointer across the
 * rail to read a label never makes the panel jump open.
 */
const TOOLTIP_OPEN_DELAY = 60;
const RAIL_EXPAND_DELAY = 400;

/**
 * How often to re-ask "is the pointer still heading for the open panel?".
 *
 * Following Amazon's approach, a queued switch is not held for a fixed period
 * and then forced through — it is re-evaluated on this interval and deferred
 * again for as long as the pointer keeps travelling toward the panel. A fixed
 * grace period is what makes menus feel like they switch too eagerly: a slow
 * diagonal takes longer than any single timeout you would want to pick.
 */
const AIM_RECHECK_MS = 90;
/**
 * If the pointer has not moved for this long it has stopped rather than
 * travelling, so a queued switch should go through. Without this, parking the
 * cursor mid-triangle would pin the wrong panel open forever.
 */
const POINTER_STALL_MS = 150;
/** Delay before closing when the pointer leaves the sidebar entirely. */
const LEAVE_CLOSE_DELAY = 180;
/** Minimum gap between pointer samples used to derive a movement vector. */
const SAMPLE_INTERVAL_MS = 50;

export const SidebarContext = createContext({});

export const useSidebar = () => useContext(SidebarContext);

// A cookie, not localStorage, so the server can read it and render the rail
// at the right width in the HTML it sends. One year, lax.
const readStoredCollapsed = () => cookies.get(STORAGE_KEY) === 'true';

const writeStoredCollapsed = (next) => cookies.set(STORAGE_KEY, String(next), {
  path: '/',
  maxAge: STORAGE_MAX_AGE,
  sameSite: 'lax',
});

const slopeBetween = (from, to) => (to.y - from.y) / (to.x - from.x);

/**
 * Is the pointer travelling into the open panel rather than along the rail?
 *
 * This is the "safe triangle" test used by Amazon's mega-menu: take the
 * triangle whose apex is where the pointer was a moment ago and whose base is
 * the panel's near edge. If the pointer is now inside that triangle it is on a
 * path to the panel, so we hold the panel open even though the cursor is
 * technically over a different row. Moving straight up or down leaves the
 * triangle immediately, so vertical travel switches panels with no lag at all.
 *
 * Expressed as slopes rather than a point-in-triangle test: the pointer is
 * inside when the line to the panel's top corner has swung anticlockwise and
 * the line to its bottom corner has swung clockwise.
 */
const isAimingAt = (rect, from, to) => {
  if (!rect || !from || !to) return false;
  // Degenerate: the pointer has not moved, so it is not heading anywhere.
  if (from.x === to.x && from.y === to.y) return false;
  // Only meaningful while the pointer is still left of the panel.
  if (to.x >= rect.left) return false;

  const topCorner = {x: rect.left, y: rect.top};
  const bottomCorner = {x: rect.left, y: rect.bottom};

  return slopeBetween(to, topCorner) < slopeBetween(from, topCorner)
    && slopeBetween(to, bottomCorner) > slopeBetween(from, bottomCorner);
};

/**
 * Owns every piece of sidebar state that more than one component needs: the
 * persisted collapsed preference, the transient hover-expansion of a collapsed
 * rail, which flyout is open, and the mobile drawer.
 *
 * Hover arbitration lives here rather than in the items because it is
 * inherently cross-component: one row's mouseleave races the next row's
 * mouseenter, and the panel they are competing over is a third element
 * entirely. Only a single owner can resolve that without flicker.
 *
 * @param {boolean} props.forceCollapsed Pin the sidebar to a rail regardless
 *   of the stored preference. Used by lesson pages, which stack a second
 *   course sidebar beside the rail and have no room for a wide primary nav.
 */
export const SidebarProvider = ({forceCollapsed = false, initialCollapsed, children}) => {
  // The shell only ever mounts on the client, after AppContext has resolved
  // the current user, so reading the preference during the first render is
  // safe and avoids a frame at the wrong width.
  // Seeded by the server from the same cookie, so the first paint is already
  // the right width and there is nothing to correct.
  const [storedCollapsed, setStoredCollapsed] = useState(
    () => (initialCollapsed ?? readStoredCollapsed()),
  );
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const [flyout, setFlyout] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const railTimer = useRef(null);
  const tooltipTimer = useRef(null);
  const aimTimer = useRef(null);
  const closeDelayTimer = useRef(null);
  const leaveTimer = useRef(null);

  // Refs mirror state that the pointer handlers need to read synchronously,
  // without re-creating those handlers on every change.
  const flyoutRef = useRef(null);
  const pendingRef = useRef(null);
  // A flyout requested while the sidebar is still a rail, waiting to open in
  // step with the rail's own expansion rather than ahead of it.
  const deferredOpenRef = useRef(null);
  const panelRectRef = useRef(null);
  const pointerRef = useRef(null);
  const priorPointerRef = useRef(null);
  const lastSampleAtRef = useRef(0);
  const lastMoveAtRef = useRef(0);

  const {asPath} = useRouter();
  const collapsed = forceCollapsed || storedCollapsed;

  useEffect(() => {
    flyoutRef.current = flyout;
  }, [flyout]);

  const clearTimer = (timer) => {
    if (!timer.current) return;
    clearTimeout(timer.current);
    // eslint-disable-next-line no-param-reassign
    timer.current = null;
  };

  const clearAllTimers = useCallback(() => {
    clearTimer(railTimer);
    clearTimer(tooltipTimer);
    clearTimer(aimTimer);
    clearTimer(closeDelayTimer);
    clearTimer(leaveTimer);
  }, []);

  useEffect(() => clearAllTimers, [clearAllTimers]);

  /* --- Flyouts -------------------------------------------------------- */

  const applyFlyout = useCallback((next) => {
    pendingRef.current = null;
    clearTimer(aimTimer);
    clearTimer(closeDelayTimer);
    setFlyout(next);
  }, []);

  /** True while the pointer is on a path into the currently open panel. */
  const isHeadingForPanel = useCallback(() => isAimingAt(
    panelRectRef.current,
    priorPointerRef.current,
    pointerRef.current,
  ), []);

  /**
   * Re-arm the aim check. Keeps deferring the queued change for as long as the
   * pointer is still travelling toward the open panel, and lets it through the
   * moment that stops being true — or the moment the pointer stops moving.
   */
  const scheduleAimCheck = useCallback(() => {
    clearTimer(aimTimer);
    aimTimer.current = setTimeout(() => {
      const stalled = performance.now() - lastMoveAtRef.current > POINTER_STALL_MS;
      if (!stalled && isHeadingForPanel()) {
        scheduleAimCheck();
        return;
      }
      applyFlyout(pendingRef.current);
    }, AIM_RECHECK_MS);
  }, [applyFlyout, isHeadingForPanel]);

  /**
   * Ask for `next` to become the open flyout. Pass null to close.
   *
   * Honoured at once unless either:
   *  - the sidebar is still a rail, in which case the panel is held back so it
   *    emerges together with the expanding rail instead of racing ahead of it;
   *  - the pointer is aiming at the panel that is already open, in which case
   *    it is queued and re-evaluated until the pointer arrives, veers off, or
   *    stops.
   *
   * @param {object|null} next Flyout descriptor, or null to close.
   * @param {boolean} [options.immediate] Skip the rail wait. Used by click
   *   handlers, where the user has committed and expects no delay.
   */
  const requestFlyout = useCallback((next, {immediate = false} = {}) => {
    const currentKey = flyoutRef.current?.key ?? null;
    const nextKey = next?.key ?? null;
    clearTimer(closeDelayTimer);
    if (currentKey === nextKey) {
      pendingRef.current = null;
      clearTimer(aimTimer);
      return;
    }

    // Hovering a rail icon: let the rail's expansion timer open this too, so
    // both surfaces start moving on the same frame.
    if (next && !immediate && collapsed && !hoverExpanded) {
      deferredOpenRef.current = next;
      return;
    }
    deferredOpenRef.current = null;

    if (!next && !immediate && flyoutRef.current?.closeDelay) {
      pendingRef.current = null;
      closeDelayTimer.current = setTimeout(() => applyFlyout(null), flyoutRef.current.closeDelay);
      return;
    }

    if (currentKey === null || !isHeadingForPanel()) {
      applyFlyout(next);
      return;
    }

    pendingRef.current = next;
    scheduleAimCheck();
  }, [applyFlyout, collapsed, hoverExpanded, isHeadingForPanel, scheduleAimCheck]);

  const closeFlyout = useCallback(() => applyFlyout(null), [applyFlyout]);

  /** Cancel any pending change — the pointer has reached somewhere safe. */
  const holdFlyout = useCallback(() => {
    pendingRef.current = null;
    clearTimer(aimTimer);
    clearTimer(closeDelayTimer);
    clearTimer(leaveTimer);
  }, []);

  /** Drop anything queued to open once the rail finishes expanding. */
  const clearDeferredOpen = () => {
    deferredOpenRef.current = null;
  };

  /** Lets the open panel publish its box for the aim test. */
  const setPanelRect = useCallback((rect) => {
    panelRectRef.current = rect;
  }, []);

  /* --- Pointer tracking ----------------------------------------------- */

  /**
   * Sampled from the sidebar and the open panel. Two samples spaced at least
   * SAMPLE_INTERVAL_MS apart give a stable direction; consecutive raw
   * mousemove events are too close together to derive one from.
   */
  const handlePointerMove = useCallback((event) => {
    const point = {x: event.clientX, y: event.clientY};
    const now = event.timeStamp;

    lastMoveAtRef.current = performance.now();
    if (now - lastSampleAtRef.current > SAMPLE_INTERVAL_MS) {
      priorPointerRef.current = pointerRef.current || point;
      lastSampleAtRef.current = now;
    }
    pointerRef.current = point;

    // Don't wait for the next re-check to notice the pointer has veered off:
    // releasing the queued change here is what makes straight vertical travel
    // between rows switch with no perceptible lag at all.
    // A live aim timer is what marks a queued change as in flight.
    if (aimTimer.current && !isHeadingForPanel()) applyFlyout(pendingRef.current);
  }, [applyFlyout, isHeadingForPanel]);

  /* --- Rail hover expansion ------------------------------------------- */

  /**
   * Called when the pointer enters the sidebar or the open panel. Always
   * cancels a pending collapse, so moving into the panel — which counts as
   * leaving the sidebar element — keeps the rail open.
   */
  const handleHoverEnter = useCallback(() => {
    clearTimer(railTimer);
    clearTimer(leaveTimer);
    if (!collapsed || hoverExpanded) return;
    railTimer.current = setTimeout(() => {
      // Both updates land in one batch, so the rail's width transition and the
      // panel's slide begin on the same frame.
      setHoverExpanded(true);
      if (deferredOpenRef.current) {
        applyFlyout(deferredOpenRef.current);
        deferredOpenRef.current = null;
      }
    }, RAIL_EXPAND_DELAY);
  }, [collapsed, hoverExpanded, applyFlyout]);

  const handleHoverLeave = useCallback(() => {
    clearTimer(railTimer);
    clearTimer(leaveTimer);
    // Pointer is off the nav entirely: drop the tooltip now and let the panel
    // and rail follow, so nothing is left hanging over the page.
    clearTimer(tooltipTimer);
    clearDeferredOpen();
    setHoveredItem(null);
    pointerRef.current = null;
    priorPointerRef.current = null;

    leaveTimer.current = setTimeout(() => {
      // A menu the user deliberately clicked open stays open until they
      // dismiss it. Only hover-opened panels are the pointer's to take away.
      if (!flyoutRef.current?.sticky) applyFlyout(null);
      setHoverExpanded(false);
    }, LEAVE_CLOSE_DELAY);
  }, [applyFlyout]);

  /* --- Rail tooltips -------------------------------------------------- */

  const requestTooltip = useCallback((key) => {
    clearTimer(tooltipTimer);
    tooltipTimer.current = setTimeout(() => setHoveredItem(key), TOOLTIP_OPEN_DELAY);
  }, []);

  const clearTooltip = useCallback((key) => {
    clearTimer(tooltipTimer);
    setHoveredItem((current) => (key === undefined || current === key ? null : current));
  }, []);

  /* --- Lifecycle ------------------------------------------------------ */

  // Reset transient state whenever we navigate. Without this, clicking a
  // flyout link leaves the panel hanging over the new page.
  useEffect(() => {
    clearAllTimers();
    clearDeferredOpen();
    applyFlyout(null);
    setHoveredItem(null);
    setHoverExpanded(false);
    setMobileOpen(false);
  }, [asPath, applyFlyout, clearAllTimers]);

  // A panel belongs to the sidebar that spawned it. If the rail stops showing
  // labels — the toggle was clicked, or a hover expansion timed out — any open
  // panel is orphaned and must go with it.
  useEffect(() => {
    if (collapsed && !hoverExpanded) applyFlyout(null);
  }, [collapsed, hoverExpanded, applyFlyout]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    document.body.classList.add('noscroll');
    return () => document.body.classList.remove('noscroll');
  }, [mobileOpen]);

  const setCollapsed = useCallback((next) => {
    setStoredCollapsed(next);
    setHoverExpanded(false);
    clearDeferredOpen();
    applyFlyout(null);
    writeStoredCollapsed(next);
  }, [applyFlyout]);

  const toggleCollapsed = useCallback(
    () => setCollapsed(!storedCollapsed),
    [setCollapsed, storedCollapsed],
  );

  const value = useMemo(() => ({
    /** True when the sidebar occupies the narrow icon rail. */
    collapsed,
    /** True when a collapsed rail is temporarily expanded by hover. */
    hoverExpanded: collapsed && hoverExpanded,
    /** True when labels should be visible, from either cause. */
    showLabels: !collapsed || hoverExpanded,
    /** True when the collapse toggle should be hidden (lesson pages). */
    forceCollapsed,
    /** {key, ...payload} of the open flyout, or null. */
    flyout,
    hoveredItem,
    mobileOpen,
    setMobileOpen,
    setCollapsed,
    toggleCollapsed,
    handleHoverEnter,
    handleHoverLeave,
    handlePointerMove,
    requestFlyout,
    holdFlyout,
    closeFlyout,
    setPanelRect,
    requestTooltip,
    clearTooltip,
  }), [
    collapsed,
    hoverExpanded,
    forceCollapsed,
    flyout,
    hoveredItem,
    mobileOpen,
    setCollapsed,
    toggleCollapsed,
    handleHoverEnter,
    handleHoverLeave,
    handlePointerMove,
    requestFlyout,
    holdFlyout,
    closeFlyout,
    setPanelRect,
    requestTooltip,
    clearTooltip,
  ]);

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
};

export default SidebarProvider;
